// SPDX-License-Identifier: AGPL-3.0-or-later

import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {PaddleSubscriptionState} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {PremiumStateResponse} from '@fluxer/schema/src/domains/premium/PremiumSchemas';
import {makeAutoObservable} from 'mobx';

const LIVE_PADDLE_STATUSES = new Set<PaddleSubscriptionState['status']>([
	'active',
	'past_due',
	'paused',
	'needs_review',
]);

class PremiumState {
	state: PremiumStateResponse | null = null;
	loading = false;
	loadedForUserId: string | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	beginLoad(userId: string): void {
		if (this.loadedForUserId !== userId) {
			this.state = null;
		}
		this.loading = true;
		this.loadedForUserId = userId;
	}

	setState(userId: string, state: PremiumStateResponse): void {
		this.state = state;
		this.loadedForUserId = userId;
		this.loading = false;
	}

	finishLoad(): void {
		this.loading = false;
	}

	// premium_purchase_disabled of this account: purchases turned off for it, or no provider sells anywhere. Whether one
	// sells to the viewer's country is ShopCatalog.payment.
	purchaseDisabledFor(userId: string | undefined): boolean {
		return (
			userId !== undefined &&
			this.loadedForUserId === userId &&
			this.state?.effective.premium_purchase_disabled === true
		);
	}

	// This account's renewing Paddle Plus; null without one or before the state is read.
	paddleFor(userId: string | undefined): PaddleSubscriptionState | null {
		return (userId !== undefined && this.loadedForUserId === userId && this.state?.paddle) || null;
	}

	// A renewing subscription that runs: no second one, and no Plus bought once beside it. A pending first payment is no
	// subscription yet; the server keeps a second checkout out meanwhile (checkout_pending).
	hasLivePaddleSubscription(userId: string | undefined): boolean {
		const status = this.paddleFor(userId)?.status;
		return status !== undefined && LIVE_PADDLE_STATUSES.has(status);
	}

	// A subscription that still holds the account, as the server's Plus hold reads it: a live one, or a canceled one whose
	// paid time has not ended.
	holdsPaddleSubscription(userId: string | undefined, now = Date.now()): boolean {
		const paddle = this.paddleFor(userId);
		if (!paddle) return false;
		if (LIVE_PADDLE_STATUSES.has(paddle.status)) return true;
		return paddle.status === 'canceled' && paddle.paid_until !== null && Date.parse(paddle.paid_until) > now;
	}

	clear(): void {
		this.state = null;
		this.loading = false;
		this.loadedForUserId = null;
	}
}

const premiumState = new PremiumState();

AccountScopedWork.registerCancellation(() => premiumState.clear());

export default premiumState;
